import { MuxStreamData, ChannelMuxVocabulary, SessionPlaybackVideo } from "types/working-model";
import { FetchError, GetJson, getToken, HvHostMap, PutJson } from "./helpers";
import store from "store/main";
import { MuxPlaybackIdRetrieveResponse } from "types/mux-playback-policy";

export function GetMuxStreamData(session_uuid: string, language: string): Promise<MuxStreamData> {
	return GetJson({
		path: `/v4/mux/metrics/${session_uuid}/${language}`,
		token: getToken()
	});
}

interface GetMuxJwtProps {
	id: string,
	userId: string,
	userToken: string,
	asAdmin?: boolean,
	thumbnail?: boolean,
	drm?: boolean,
	thumbParams?: {
		time: number;
		width?: number;
		height?: number;
		fit_mode?: string;
	},
	landingEvent?: string;
	eventUuid?: string;
}
export function GetMuxJwt({
	id,
	userId,
	userToken,
	asAdmin = false,
	thumbnail = false,
	drm = false,
	thumbParams,
	eventUuid
}: GetMuxJwtProps): Promise<{ token: string, drmToken?: string }> {
	// this just exists to bust the cache on the route
	const params = new URLSearchParams({ u: userId });

	if (thumbnail) {
		params.append('thumbnail', 'true');

		if (thumbParams) {
			params.append('thumb_params', JSON.stringify(thumbParams));
		}
	}

	if (drm) {
		params.append('drm', 'true');
	}

	if (eventUuid) {
		params.append('eventUuid', eventUuid);
	}

	// request uses browser cache so we don't need to go overboard trying to store this
	// just cache on the user's uuid in case someone signs out
	return fetch(`${HvHostMap.sessionTracking}/mux-jwt/${id}?${params.toString()}`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
			[asAdmin ? 'Bl-Token' : 'Bl-Profile-Token']: userToken
		},
		credentials: 'include'
	}).then(res => {
		if (!res.ok) {
			throw new FetchError({ status: res.status, error: 'Failed to fetch JWT for playback URL' });
		}

		return res.json();
	});
}

// we don't necessarily know at this point whether the playback urls require signatures or not
// until we can refactor this. For now, we'll just check if the playback url is HLS, if so we'll check
// if a signature is required, if yes we'll generate one, test it, then return it. Otherwise we'll just return the url.
export const getPlayableUrlWithToken = async (uri: string, drmEnabled?: boolean, muxToken?: string, eventUuid?: string) => {
	try {
		// this is not an HLS video, just return the url
		if (!uri.includes('.m3u8')) {
			return {
				url: uri,
				drmToken: undefined,
				playbackId: undefined,
				drmRequired: false,
				ok: true
			};
		}

		// this is not a mux video, just return the url
		if (!(uri.includes('mux.com') || uri.includes('videodelivery.brandlive'))) {
			return {
				url: uri,
				drmToken: undefined,
				playbackId: undefined,
				drmRequired: false,
				ok: true
			};
		}

		const state = store.getState();
		const adminToken = state.AuthReducer.token;
		const profileToken = state.LiveEventReducer.blProfileUserToken;
		const user = state.AuthReducer.user;
		const profile = state.LiveEventReducer.blProfileUser;

		// DRM-enabled channels cannot use the custom mux playback domain
		const url = new URL(uri);
		if (drmEnabled) {
			url.hostname = 'stream.mux.com';
		}

		// check if the playback URL is valid
		// if a token is required this will return a 403
		const response = await fetch(url.href, { method: 'HEAD' });
		const id = url.pathname.replace(/^\/|\.m3u8/g, '');

		// we attempted to load the URL with no signature but it failed with a 403
		if (response.status === 403) {
			const userId = user?.email || profile?.uuid || '';
			const userToken = adminToken || profileToken || '';

			// try passed-in muxToken first if available
			if (muxToken) {
				url.searchParams.set('signature', muxToken);
				const muxTokenResponse = await fetch(url.href, { method: 'HEAD' });

				if (muxTokenResponse.ok) {
					return {
						url: url.href,
						drmToken: undefined,
						playbackId: id,
						drmRequired: false,
						ok: true
					};
				}
				url.searchParams.delete('signature');
			}

			// get a header token...
			const tokens = await GetMuxJwt({ id, userId, userToken, asAdmin: !!adminToken, eventUuid });

			url.searchParams.set('token', tokens.token);
			const signedUrl = url.href;

			// confirm that the signed URL works
			const retry = await fetch(signedUrl, { method: 'HEAD' });

			// respond with the signed URL if it works and whether or not DRM is required
			if (retry.ok) {
				return {
					url: url.href,
					drmToken: tokens.drmToken,
					playbackId: id,
					drmRequired: !!tokens.drmToken,
					ok: true
				};
			}
		}

		// initial response was not Forbidden - no signature required
		return {
			url: url.href,
			drmToken: undefined,
			playbackId: id,
			drmRequired: false,
			ok: response.ok
		};
	} catch (e) {
		console.error(e);
		return { url: uri, drmToken: undefined, playbackId: undefined, drmRequired: false };
	}
};

// we don't necessarily know at this point whether the playback urls require signatures or not
// until we can refactor this. For now, we'll just check if the playback url is HLS, if so we'll check
// if a signature is required, if yes we'll generate one, test it, then return it. Otherwise we'll just return the url.
export const getThumbnailUrlWithToken = async (uri: string, eventUuid?: string) => {
	try {
		if (!uri.includes('.webp')) {
			return uri;
		}

		if (!(uri.includes('mux.com') || uri.includes('videodelivery.brandlive'))) {
			return uri;
		}

		const url = new URL(uri);
		const state = store.getState();
		const adminToken = state.AuthReducer.token;
		const profileToken = state.LiveEventReducer.blProfileUserToken;
		const user = state.AuthReducer.user;
		const profile = state.LiveEventReducer.blProfileUser;

		const response = await fetch(url, { method: 'HEAD' });

		// the request was forbidden - this happens when the thumbnail is private/DRM-enabled
		if (response.status === 403) {

			// DRM-enabled channels cannot use the custom mux playback domain, so 
			// fall back to the default mux domain 
			url.hostname = 'image.mux.com';
			const id = new URL(url).pathname.split('/')[1];
			const userId = user?.email || profile?.uuid || '';
			const userToken = adminToken || profileToken || '';
			const headers = await GetMuxJwt({ id, userId, userToken, asAdmin: !!adminToken, thumbnail: true, eventUuid });
			const params = new URLSearchParams({ token: headers.token });
			const signedUrl = `${url}?${params.toString()}`;
			const retry = await fetch(signedUrl);
			if (retry.ok) {
				return signedUrl;
			} else {
				return new Error(`Failed to fetch thumbnail with signed URL: ${retry.status} ${retry.statusText}`);
			}
		}

		return uri;
	} catch (e) {
		console.error(e);
		return uri;
	}
};

export function UpdateChannelMuxVocabulary(channel: number, muxVocabulary: ChannelMuxVocabulary): Promise<ChannelMuxVocabulary> {
	return PutJson({
		path: `/v4/mux/${channel}/vocabulary`,
		token: getToken(),
		data: muxVocabulary
	});
}

export async function GetMuxPlaybackIdDetails(muxPlaybackId: string, asAdmin: boolean, userToken: string): Promise<MuxPlaybackIdRetrieveResponse | null> {
	try {
		const resp = await fetch(`${HvHostMap.sessionTracking}/playback-policy/${muxPlaybackId}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				[asAdmin ? 'Bl-Token' : 'Bl-Profile-Token']: userToken
			},
			cache: 'force-cache',
		});
		if (!resp.ok || resp.status !== 200) {
			return null;
		}
		const json = await resp.json();
		return json;
	} catch (error) {
		console.error(error);
		return null;
	}
}
